"use client";
import React, { useState } from "react";
import Breadcrumb, { type BreadcrumbPath } from "@/app/components/breadcrumb.client";
import ICMWrapper from "../components/icm-wrapper";
import { Input } from "@/stories/input/input";
import Image from "next/image";
import CalculatorIcon from "../assets/icons/calculator";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { getCalculatorData } from "../api/service/homepage";
import LoadingIcon from "../assets/icons/loading";
import SearchIcon from "../assets/icons/search";

interface CalculatorDetails {
    title: string;
    subText: string;
}

export interface FDReturnsCalculator {
    calculatorId: string;
    category: string;
    deleted: boolean;
    detailSection: CalculatorDetails[];
    disabled: boolean;
    frequentlyUsed: number;
    iconUrl: string;
    priority: number;
    subTitle: string;
    title: string;
    updatedAt: string;
    interestRate: string;
    issuerLogo: string;
    issuerName: string;
    tenurePeriod: string;
}

const FDCards = ({ calcData, isSmallerCards }: { calcData: FDReturnsCalculator[], isSmallerCards: boolean }) => {
    const router = useRouter();

    // Card component for each calculator
    const CalculatorCard = ({ bank }: { bank: FDReturnsCalculator }) => {
        const isPriorityZero = bank?.priority === 0;

        return (
            <div
                className={`${isSmallerCards ? 'md:max-h-[134px]' : ''} cursor-pointer rounded-xl border border-neutral-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${
                    isPriorityZero
                        ? "mb-6 flex flex-col items-start justify-center md:mb-0 md:mr-[42px] md:max-h-[300px] md:w-[372px]"
                        : "flex items-center md:min-w-[378px]"
                }`}
                onClick={() =>
                    router.push(
                        `/calculators/${bank.title.trim().split(" ").join("-").toLowerCase()}`,
                    )
                }
            >
                {isPriorityZero ? (
                    <>
                        <CalculatorIcon />
                        <div className="mt-6">
                            <h3
                                className={`font-semibold leading-[120%] tracking-[-0.24px] ${isPriorityZero ? "mb-3 text-3xl" : "text-lg"}`}
                            >
                                {bank?.title}
                            </h3>
                            <p className="text-sm leading-[160%] tracking-[0.14px]">
                                {bank?.subTitle}
                            </p>
                        </div>
                    </>
                ) : (
                    <>
                        <div
                            className={`max-h-[58px] min-h-[58px] min-w-[58px] max-w-[58px] rounded-xl border border-neutral-100 p-[11.54px] md:max-h-[84px] md:min-h-[84px] md:min-w-[84px] md:max-w-[84px] md:p-4 ${isPriorityZero ? "py-9" : "mr-4"}`}
                        >
                            <Image
                                height={84}
                                width={84}
                                src={typeof bank.iconUrl === "string" ? bank.iconUrl : ""}
                                alt={`${bank.issuerName} logo`}
                            />
                        </div>
                        <div>
                            <h3 className={`mb-3 text-md font-semibold leading-[130%] md:text-lg`}>
                                {bank.title}
                            </h3>
                            <p className="text-sm leading-[140%] tracking-[0.14px]">
                                {bank.subTitle}
                            </p>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="mb-8 flex min-h-[297px] w-full flex-col justify-between md:mb-32 md:flex-row">
            {(calcData.find(
                (item: FDReturnsCalculator) => item.priority === 0,
            ) as FDReturnsCalculator) && (
                <CalculatorCard
                    bank={
                        calcData.find(
                            (item: FDReturnsCalculator) => item.priority === 0,
                        ) as FDReturnsCalculator
                    }
                />
            )}

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                {calcData &&
                    calcData
                        ?.filter((bank: FDReturnsCalculator) => bank.priority !== 0)
                        .sort(
                            (a: FDReturnsCalculator, b: FDReturnsCalculator) =>
                                a.priority - b.priority,
                        )
                        .map((bank: FDReturnsCalculator) => (
                            <CalculatorCard key={bank.calculatorId} bank={bank} />
                        ))}
            </div>
        </div>
    );
};

const Calculator = () => {
    const [searchQuery, setSearchQuery] = useState(""); // State to store the search query

    const { data: calcData, isFetched: isCalcFetched } = useQuery({
        queryKey: ["calculatorData"],
        queryFn: getCalculatorData,
        retry: 1,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    // Function to handle input changes
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQuery(e.target.value); // Trim whitespace and update state
    };

    // Filter data based on search query
    const filteredData: FDReturnsCalculator[] = calcData?.data?.filter((item: FDReturnsCalculator) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const isSmallerCards = filteredData?.every(
        (item: FDReturnsCalculator) => item.priority !== 0,
    );

    return (
        <ICMWrapper>
            <div className="w-full md:px-36">
            <Breadcrumb
                paths={
                    [
                        { label: "Home", route: "/" },
                        { label: "Calculators" },
                    ] as BreadcrumbPath[]
                }
            />
            {!isCalcFetched ? (
                <div className="flex items-center justify-center py-10">
                    <LoadingIcon className="animate-spin" />
                </div>
            ) : (
                <div>
                    <div className="flex flex-col items-center justify-between gap-8 pt-s7 md:flex-row md:gap-0 md:py-14">
                        <div className="flex w-full flex-col gap-3 md:w-auto md:gap-8">
                            <h1 className="text-3xl font-semibold leading-[120%] tracking-[-0.48px] md:text-6xl">
                                Financial Planning Calculators
                            </h1>

                            {/* Search Input */}
                            <Input
                                placeholder="Search"
                                icon={
                                    <SearchIcon size={10} className="py-1 pl-1 text-neutral-300" />
                                }
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="rounded-xl border border-neutral-100 placeholder:text-neutral-300 md:w-11/12"
                            />
                        </div>

                        <Image
                            src={`${process.env.ICM_PUBLIC_ASSETS_URL}images/webp/calc-landing.webp`}
                            alt="Calculator illustration"
                            className="h-[241px] w-[335px]"
                            objectFit="contain"
                            width={335}
                            height={241}
                            priority
                        />
                    </div>

                    {filteredData.length > 0 ? (
                        <div className="mt-6 flex flex-col gap-6 md:mt-0">
                            <h2 className="text-3xl font-semibold leading-[120%] tracking-[-0.48px]">
                                FD Calculator
                            </h2>

                            {/* Pass filtered data to FDCards */}
                            <FDCards calcData={filteredData || []} isSmallerCards={isSmallerCards} />
                        </div>
                    ) : (
                        <h2 className="text-2xl text-center py-10 font-semibold leading-[120%] tracking-[-0.48px] md:text-4xl">
                            Oops no result found!!!
                        </h2>
                    )}
                </div>
            )}
            </div>
        </ICMWrapper>
    );
};

export default Calculator;
